import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../components/Loader";
import { Box, Button, Grid, Typography } from "@mui/material";
import BoxCheckInput from "../../../components/BoxCheckInput";
import { useFormik } from "formik";
import AuthorizedSignatoryDetails from "../components/AuthorizedSignatoryDetails";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import ApplicantDetailIndividual from "./ApplicantDetailIndivdual";
import {
  deleteSignatory,
  setActiveStep,
  setApplicantTotal,
  setKycVerificationActiveStep,
  setStepsCompleted,
} from "../new-booking-form.slice";
import { useEffect, useState } from "react";
import { APPLICANT_NUMBER_WORD } from "../../../config/constant";
import DeleteIcon from "../../../assets/icons/delete-red.svg";
import DeleteConfirmationModal from "../../../shared-components/delete-confirmation-modal/DeleteConfirmationModal";
import { route } from "../../../services/apiRoutes";
import { useParams } from "react-router-dom";
import { getErrorMessage } from "../../../utilis/common-funtions";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import SelectInput from "../../../components/SelectInput";

function ApplicantDetails({ applicantNo }) {
  const { t } = useTranslation();
  const { oppId } = useParams();
  const dispatch = useDispatch();
  const {
    isLoading,
    masterData,
    applicantData,
    activeStep,
    kycVerificationActiveStep,
    applicantTotal,
    stepsCompleted,
  } = useSelector((state) => state?.newBookingForm);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbarConfig, setSnackBarconfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const currentApplicant = applicantData?.[`applicant${applicantNo}`];
  const personalDetails = currentApplicant?.personalDetails;

  const formik = useFormik({
    initialValues: {
      relationship: null,
      residentStatus: "",
    },
    validationSchema: Yup.object().shape({
      relationship: Yup.string().when([], {
        is: () => applicantNo === 2,
        then: (schema) =>
          schema.required(
            t("applicantDetails.fields.relationship.validation.required")
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      residentStatus: Yup.string().required(
        t("applicantDetails.fields.residentStatus.validation.required")
      ),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false);
    },
  });

  const handleOnChange = (e, index, key, list) => {
    if (!personalDetails?.residentStatus) {
      let selectedValue = list[index]?.value;
      formik.setFieldValue(key, selectedValue);
    }
  };

  useEffect(() => {
    if (personalDetails) {
      formik.setFieldValue(
        "residentStatus",
        personalDetails?.residentStatus ?? ""
      );
      formik.setFieldValue(
        "relationship",
        personalDetails?.relationship ?? null
      );
    } else {
      dispatch(setKycVerificationActiveStep(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, personalDetails]);

  const handleDeleteAuthorizedSignatory = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteAuthorizedSignatoryCancel = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteAuthorizedSignatoryConfirm = async () => {
    setIsDeleting(true);
    const deleteIndex = activeStep;

    try {
      if (applicantData?.[`applicant${applicantNo}`]) {
        await dispatch(
          deleteSignatory({
            path: route.DELETE_APPLICANT,
            payload: {
              opportunityId: oppId,
              applicantId: applicantNo,
            },
            opportunityId: oppId,
          })
        ).unwrap();
      }
    } catch (error) {
      setSnackBarconfig({
        open: true,
        message: getErrorMessage(error),
        severity: "error",
      });
    } finally {
      const updatedStepsCompleted = Object.keys(stepsCompleted)
        .filter((key) => Number(key) !== deleteIndex)
        .map((key, newIndex) => [newIndex, stepsCompleted[key]])
        .reduce((acc, [newKey, value]) => {
          acc[newKey] = value;
          return acc;
        }, {});

      dispatch(setApplicantTotal(applicantTotal - 1));
      dispatch(setStepsCompleted(updatedStepsCompleted));
      dispatch(setKycVerificationActiveStep(1));
      dispatch(setActiveStep(activeStep - 1));
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackBarconfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={handleDeleteAuthorizedSignatoryCancel}
        onConfirm={handleDeleteAuthorizedSignatoryConfirm}
        title={t("deleteIndividualApplicantModal.title")}
        message={t("deleteIndividualApplicantModal.message", {
          no: applicantNo,
          sup: APPLICANT_NUMBER_WORD[applicantNo - 1],
        })}
        applicantNo={applicantNo}
        loading={isDeleting}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          sx={{
            color: "#1C252E",
            fontSize: "16px",
            fontWeight: "600",
            lineHeight: "36px",
            textAlign: "center",
            flex: 1,
          }}
          dangerouslySetInnerHTML={{
            __html:
              applicantNo === 1
                ? t("applicantDetailIndvidual.title.primary")
                : t("applicantDetailIndvidual.title.other", {
                    number: applicantNo,
                    sup: `<sup>${APPLICANT_NUMBER_WORD[applicantNo - 1]}</sup>`,
                  }),
          }}
        />

        {applicantNo === applicantTotal && applicantTotal > 1 && (
          <Button
            onClick={handleDeleteAuthorizedSignatory}
            startIcon={
              <img
                src={DeleteIcon}
                alt="Delete"
                style={{ width: 20, height: 20 }}
              />
            }
            sx={{
              color: "#d32f2f",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#ffebee",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                display: { xs: "none", sm: "inline" },
              }}
            >
              {t("commonLabels.deleteApplicant")}
            </Box>
          </Button>
        )}
      </Box>
      {kycVerificationActiveStep === 0 ? (
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={0}>
            {applicantNo === 2 && (
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6} mb={2}>
                <SelectInput
                  field={{
                    name: "relationship",
                    label: t("applicantDetails.fields.relationship.label"),
                    required: true,
                    readonly: false,
                    options: masterData?.Relation_with_Applicant,
                    placeholder: "Select",
                  }}
                  formik={formik}
                />
              </Grid>
            )}
            {!personalDetails?.residentStatus && (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <BoxCheckInput
                  field={{
                    name: "residentStatus",
                    label: t("applicantDetails.fields.residentStatus.label"),
                    required: true,
                    readonly: false,
                    options: masterData?.Resident_Status,
                    grid: true,
                    xsCol: 4,
                    smCol: 4,
                    mdCol: 4,
                    lgCol: 4,
                    mb: 0,
                    handleOnChange,
                  }}
                  formik={formik}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <AuthorizedSignatoryDetails
                applicantNo={applicantNo}
                residentStatus={formik?.values?.residentStatus}
                relationship={formik?.values?.relationship}
                individualErrors={formik.errors}
                isIndividualFlow={true}
              />
            </Grid>
          </Grid>
        </form>
      ) : (
        <ApplicantDetailIndividual applicantNo={applicantNo} />
      )}
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
}

export default ApplicantDetails;
